import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LogOut, LayoutDashboard, MessageCircle, Target, BookOpen, Sparkles, Menu, X, TrendingUp, Video, Calendar, ShieldCheck, Gift, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG';

const navItems = [
  { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard', primary: true },
  { to: '/app/coach', label: 'AI Coach', icon: MessageCircle, testid: 'nav-coach', primary: true },
  { to: '/app/pregame', label: 'Pre-Game', icon: Target, testid: 'nav-pregame', primary: true },
  { to: '/app/journal', label: 'Journal', icon: BookOpen, testid: 'nav-journal', primary: true },
  { to: '/app/progress', label: 'Progress', icon: TrendingUp, testid: 'nav-progress', primary: true },
  { to: '/app/videos', label: 'Videos', icon: Video, testid: 'nav-videos', primary: false },
  { to: '/app/book', label: 'Book Coach', icon: Calendar, testid: 'nav-book', primary: false },
  { to: '/app/refer', label: 'Refer Friends', icon: Gift, testid: 'nav-refer', primary: false },
  { to: '/app/pricing', label: 'Pricing', icon: Sparkles, testid: 'nav-pricing', primary: false },
  { to: '/app/apex-six', label: 'Apex Six', icon: Layers, testid: 'nav-apex-six', primary: false },
];

const adminNavItem = { to: '/app/admin', label: 'Admin', icon: ShieldCheck, testid: 'nav-admin', primary: false };

const bottomNavItems = navItems.filter((n) => n.primary);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const linkBase = 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors';
  const linkActive = 'bg-[#004C54] text-white';
  const linkIdle = 'text-foreground hover:bg-[#F0F4F4]';

  return (
    <div className="min-h-screen bg-[#FAFBFB] text-foreground" data-testid="app-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate('/app/dashboard')}
            className="flex items-center gap-2.5"
            data-testid="app-logo"
          >
            <img src={LOGO_URL} alt="Apex" className="h-8 w-8 rounded-md object-contain" />
            <span className="font-heading font-bold text-[14px] tracking-tight hidden sm:inline">
              Apex Athletic Mind
            </span>
          </button>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name || 'You'} className="h-8 w-8 rounded-full object-cover border border-border" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-[#004C54] text-white flex items-center justify-center text-xs font-bold">
                  {(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-medium max-w-[160px] truncate" data-testid="user-name">
                {user?.name || user?.email}
              </span>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-foreground hover:bg-secondary/60"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 -mr-2 rounded-lg hover:bg-secondary/60"
              aria-label="Toggle menu"
              data-testid="app-mobile-toggle"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-white">
            <div className="px-4 py-3 flex flex-col gap-1" data-testid="app-mobile-menu">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
                  data-testid={`mobile-${it.testid}`}
                >
                  <it.icon className="h-4 w-4" />
                  {it.label}
                </NavLink>
              ))}
              {user?.is_admin && (
                <NavLink
                  to={adminNavItem.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle} mt-1 border-t border-border pt-3`}
                  data-testid={`mobile-${adminNavItem.testid}`}
                >
                  <adminNavItem.icon className="h-4 w-4" />
                  {adminNavItem.label}
                </NavLink>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main layout: sidebar (md+) + content */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 sm:py-8 grid md:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden md:block">
          <nav className="sticky top-20 flex flex-col gap-1" aria-label="App navigation">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
                data-testid={it.testid}
              >
                <it.icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            ))}
            {user?.is_admin && (
              <>
                <div className="mt-2 mb-1 px-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground/45 font-mono-accent">
                  Admin
                </div>
                <NavLink
                  to={adminNavItem.to}
                  className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
                  data-testid={adminNavItem.testid}
                >
                  <adminNavItem.icon className="h-4 w-4" />
                  {adminNavItem.label}
                </NavLink>
              </>
            )}
          </nav>
        </aside>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-border safe-bottom"
        aria-label="Bottom navigation"
        data-testid="app-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {bottomNavItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium ${
                  isActive ? 'text-[#004C54]' : 'text-foreground/60'
                }`
              }
              data-testid={`bottom-${it.testid}`}
            >
              <it.icon className="h-5 w-5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="md:hidden h-[68px]" aria-hidden="true" />
    </div>
  );
}
